import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import { AuthService, ConfigService } from '../../../core';
import { EnumCadastro as EnumTagCadastro } from '../../../domains/environment/tags/enums/cadastro.enum';
import { IModalSettings } from '../../../domains/shared/components/modal/modal.component';
import {
  INovoDatepickerSettings,
  INovoDatepickerValue,
  NovoDatepickerComponent
} from '../../../domains/shared/components/novo-datepicker';
import { parseLocalDate, toLocalDateString } from '../../../domains/shared/components/novo-datepicker/novo-datepicker.utils';
import { EnumTipoSegmento } from '../../../domains/shared/components/segmentos-multi-select/enums/segmento-tipo.enum';
import { TagsMultiSelectComponent } from '../../../domains/shared/components/tags-multi-select/tags-multi-select.component';
import { EnumUsuarioPerfil } from '../../../domains/shared/components/usuario-multi-select/enums/usuario-perfil.enum';
import { ITokenItem } from '../../../domains/shared/components/tokenfield/tokenfield.component';
import { EnumSituacao as EnumTagSituacao } from '../../../domains/shared/enums/situacao.enum';
import { isPeriodicidadePeriodo, periodicidadeMensal } from '../../../shared';
import { datepickerModePeriodicidade } from '../../../shared/components/datepicker';
import { ConfigEmpresa } from '../../configuracoes/app/empresa';
import { ConfigPedido } from '../../configuracoes/app/pedido';
import { KPI_CUSTOMER_SHARE, KPI_RAP } from '../../kpis';
import {
  PedidoReferenciaData,
  referenciaDataConfirmacao,
  referenciaDataEmissao
} from '../referencia-data';
import { PedidoFilter } from './pedido-filter';
import { PedidoFilterStorageService } from './pedido-filter-storage.service';

interface Config {
  canShowGruposRentabilidadeMultiselect?: boolean;
}

@Component({
  selector: 'sgt-pedido-filter',
  templateUrl: './pedido-filter.component.html',
  styleUrls: ['./pedido-filter.component.scss']
})
export class PedidoFilterComponent implements OnInit {
  config: Config = {};
  configEmpresa: ConfigEmpresa;
  configPedido: ConfigPedido;
  isRepresentante: boolean;

  @Input() filter: PedidoFilter;
  @Input() isMobile = false;
  @Output() filterInitialized = new EventEmitter<void>();
  @Output() filterChange = new EventEmitter<any>();

  filterCount: number;
  tipoSegmento = EnumTipoSegmento;
  readonly EnumUsuarioPerfil = EnumUsuarioPerfil;
  readonly EnumSituacao = EnumTagSituacao;

  private readonly advancedFilterFields = [
    'numero',
    'numeroEcommerce',
    'numeroErp',
    'numeroCanalVenda',
    'origensLancamentos',
    'idsCanaisVenda',
    'operacoesVenda',
    'pessoa',
    'idsClassificacoesFaturamentosPretendidas',
    'indicadoresCustomersSharesPretendidos',
    'setoresGep',
    'idsAreasCampanhas',
    'idsLocaisFaturamentos',
    'operacoesContaOrdem',
    'statusesClicheria',
    'tiposPagamentos',
    'idsFormasPagamentos',
    'idsCondicoesPagamentos',
    'cuponsDesconto',
    'idsEstadosClientes',
    'cidadeCliente',
    'idsTagsClientes',
    'idsTagsItens',
    'idsGruposRentabilidade',
    'idsGerentes',
    'idsAtendentes'
  ];

  getFilterCount(): void {
    this.filterCount = this.advancedFilterFields.filter((field) => {
      const v = this.filter[field];
      return Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined;
    }).length;
  }

  // Modal Sidebar de Filtros
  isFilterModalOpen = false;
  filterModalSettings: IModalSettings = {
    position: 'right',
    width: '500px',
    hasHeader: true,
    hasFooter: true,
    showCloseButton: true,
    allowEscapeToClose: false
  };

  // Referências de data
  referenciasData: PedidoReferenciaData[] = [
    referenciaDataEmissao,
    referenciaDataConfirmacao
  ];

  // Data Vigência Datepicker
  datepickerVigenciaSettings: INovoDatepickerSettings = {
    mode: datepickerModePeriodicidade.id,
    periodicidades: [
      'diaria',
      'mensal',
      'trimestral',
      'semestral',
      'anual',
      'periodo'
    ]
  };
  @ViewChild('dataVigenciaDatepicker')
  dataVigenciaDatepicker: NovoDatepickerComponent;

  tagSituacaoAtivo = EnumTagSituacao.ATIVO;
  tagCadastroCliente = EnumTagCadastro.CLIENTE;
  @ViewChild('tagsClientesMultiSelect')
  tagsClientesMultiSelect: TagsMultiSelectComponent;
  tagCadastroItem = EnumTagCadastro.ITEM;
  @ViewChild('tagsItensMultiSelect')
  tagsItensMultiSelect: TagsMultiSelectComponent;

  // Options
  @Input() isShowSegmentosClientesFilter = true;
  @Input() isShowSegmentosItensFilter = true;
  @Input() isShowClienteFilter = true;
  @Input() isShowRepresentantesFilter = true;

  vigenciaValue: INovoDatepickerValue | null = null;
  isFirstChangeClassificacoesClientesMultiselect = true;
  cuponsDescontoTokens: ITokenItem[] = [];

  // KPIs
  KPI_RAP = KPI_RAP;
  KPI_CUSTOMER_SHARE = KPI_CUSTOMER_SHARE;

  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private pedidoFilterStorageService: PedidoFilterStorageService
  ) {}

  ngOnInit() {
    this.configEmpresa = this.configService.getEmpresa();
    this.configPedido = this.configService.getPedido();
    this.isRepresentante = this.authService.isRepresentante();
    this.setConfig();

    this.pedidoFilterStorageService.applyFilter(this.filter);
    this.cuponsDescontoTokens = (this.filter.cuponsDesconto || []).map(
      (label, i) => ({ id: i + 1, label })
    );

    if (!this.filter.possuiPeriodicidadeVigencia()) {
      const data = new Date();
      data.setDate(1);
      this.filter.periodicidadeVigencia = periodicidadeMensal.id;
      this.filter.dataInicioVigencia = data;
    }

    this.vigenciaValue = this.buildVigenciaValue();
    this.getFilterCount();
    setTimeout(() => this.filterInitialized.emit());
  }

  private setConfig() {
    this.config = {
      canShowGruposRentabilidadeMultiselect: this.authService.podeVisualizarQV()
    };
  }

  private buildVigenciaValue(): INovoDatepickerValue | null {
    const p = this.filter.periodicidadeVigencia;
    if (!p) {
return null;
}

    if (isPeriodicidadePeriodo(p)) {
      const {
        dataInicioVigenciaPeriodo: start,
        dataTerminoVigenciaPeriodo: end
      } = this.filter;
      if (!start || !end) {
return null;
}
      return {
        date: [toLocalDateString(new Date(start)), toLocalDateString(new Date(end))],
        periodicidade: p
      };
    }

    const d = this.filter.dataInicioVigencia;
    if (!d) {
return null;
}
    return { date: toLocalDateString(new Date(d)), periodicidade: p };
  }

  private applyVigenciaToFilter(periodicidade: INovoDatepickerValue | null) {
    if (!periodicidade) {
      this.filter.periodicidadeVigencia = null;
      this.filter.dataInicioVigencia = null;
      this.filter.dataInicioVigenciaPeriodo = null;
      this.filter.dataTerminoVigenciaPeriodo = null;
      return;
    }

    const isPeriodo = isPeriodicidadePeriodo(periodicidade.periodicidade);
    const dates = periodicidade.date as string[];

    this.filter.periodicidadeVigencia = periodicidade.periodicidade;
    this.filter.dataInicioVigencia =
      !isPeriodo && periodicidade.date
        ? parseLocalDate(periodicidade.date as string)
        : null;
    this.filter.dataInicioVigenciaPeriodo =
      isPeriodo && dates[0] ? parseLocalDate(dates[0]) : null;
    this.filter.dataTerminoVigenciaPeriodo =
      isPeriodo && dates[1] ? parseLocalDate(dates[1]) : null;
  }

  onCuponsDescontoChange(tokens: ITokenItem[]): void {
    this.filter.cuponsDesconto = tokens.map((t) => t.label);
    this.cuponsDescontoTokens = tokens;
  }

  refresh() {
    this.filterChange.emit();
  }

  openFilterModal() {
    this.isFilterModalOpen = true;
  }

  onCloseFilterModal() {
    this.isFilterModalOpen = false;
  }

  applyFilterAndClose() {
    this.filter.idPessoa = this.filter.pessoa?.idPessoa ?? null;
    this.filter.idCidadeCliente = this.filter.cidadeCliente?.id ?? null;
    this.filterChange.emit();
    this.getFilterCount();
    this.isFilterModalOpen = false;
  }

  onChangeReferenciaData(_: number) {
    this.refresh();
  }

  onDateChange(periodicidade: INovoDatepickerValue | null) {
    this.applyVigenciaToFilter(periodicidade);
    this.refresh();
  }

  onStatusChange(_: number[]) {
    this.refresh();
  }

  onTiposVendasChange(_: number[]) {
    this.refresh();
  }

  onRepresentantesChange(_: number[]) {
    this.refresh();
  }

  onSegmentosClientesChange(_: number[]) {
    this.refresh();
  }

  onSegmentosItensChange(_: number[]) {
    this.refresh();
  }

  onClearFilter() {
    this.filter.numero = null;
    this.filter.numeroEcommerce = null;
    this.filter.numeroErp = null;
    this.filter.numeroCanalVenda = null;
    this.filter.origensLancamentos = [];
    this.filter.idsCanaisVenda = [];
    this.filter.operacoesVenda = [];
    this.filter.pessoa = null;
    this.filter.idPessoa = null;
    this.filter.idsClassificacoesFaturamentosPretendidas = [];
    this.filter.indicadoresCustomersSharesPretendidos = [];
    this.filter.setoresGep = [];
    this.filter.idsAreasCampanhas = [];
    this.filter.idsLocaisFaturamentos = [];
    this.filter.operacoesContaOrdem = [];
    this.filter.statusesClicheria = [];
    this.filter.tiposPagamentos = [];
    this.filter.idsFormasPagamentos = [];
    this.filter.idsCondicoesPagamentos = [];
    this.filter.cuponsDesconto = [];
    this.cuponsDescontoTokens = [];
    this.filter.idsEstadosClientes = [];
    this.filter.cidadeCliente = null;
    this.filter.idCidadeCliente = null;
    this.filter.idsTagsClientes = [];
    this.filter.idsTagsItens = [];
    this.filter.idsGruposRentabilidade = [];
    this.filter.idsGerentes = [];
    this.filter.idsAtendentes = [];
    this.getFilterCount();
    this.isFilterModalOpen = false;
    this.refresh();
  }
}
